import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@ophelos/ui/components/resizable';
import { ScrollArea } from '@ophelos/ui/components/scroll-area';
import { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/customer-auth-context';
import { carriesPaymentLanding, redirectQuery } from '@/lib/redirect';
import { ThemeProvider } from './components/layout/ThemeProvider';
import { PreviewNavigationBlocker } from './components/preview/PreviewNavigationBlocker';
import { KeyedByDebt } from './components/routing/KeyedByDebt';
import { AIMobileFullScreen, AIPanelContent, AITriggerButton } from './components/ui/AIHelperCompact';
import { AIContextProvider, useAIPanel } from './contexts/AIContext';
import { ConfigurationProvider, useConfiguration } from './contexts/ConfigurationContext';
import { CookieConsentProvider } from './contexts/CookieConsentContext';
import { DebtDataProvider } from './contexts/DebtDataContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { MOBILE_QUERY, useMediaQuery } from './hooks/useMediaQuery';
import './lib/i18n'; // Initialize i18n
import Account from './pages/Account';
import CallbackPage from './pages/auth/CallbackPage';
import AccountPayment from './pages/AccountPayment';
import BudgetAssessment from './pages/BudgetAssessment';
import ClickToPay from './pages/ClickToPay';
import CookiePolicy from './pages/CookiePolicy';
import Dashboard from './pages/Dashboard';
import DebtCleared from './pages/DebtCleared';
import PaymentHistory from './pages/PaymentHistory';
import Login from './pages/Login';
import NotFoundPage from './pages/NotFoundPage';
import Payment from './pages/Payment';
import PaymentPlan from './pages/PaymentPlan';
import { Loader } from '@ophelos/ui/components/loader';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { isPreviewMode, isLoading: configLoading } = useConfiguration();
  const location = useLocation();

  // Preserve a payment deep link across login so an operator-sent link lands the
  // customer on the right page after they authenticate. Both payment surfaces qualify: an
  // offer covering the whole account has no case to name, so its link is account-scoped, and
  // dropping its landing here would put the customer on pay-in-full with no sign that a tab
  // had been asked for. `/payment-plan/` is deliberately absent — it has no landing to carry.
  const deepLinkQuery = carriesPaymentLanding(location.pathname)
    ? redirectQuery(location.pathname + location.search)
    : '';

  // In preview mode only, skip all auth checks (for iframe preview)
  if (isPreviewMode) {
    return <>{children}</>;
  }

  // Show loader while auth or config is loading
  if (authLoading || configLoading) {
    return <Loader fullScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to={`/login${deepLinkQuery}`} replace />;
  }

  // Stage-2 magic-auth / claim via user_scope_links is removed — do not force
  // limited Stage-1 sessions onto /verification (that flow now dead-ends).
  return <>{children}</>;
}

// App content with resizable panels (must be inside AIContextProvider)
/**
 * Each new page starts at the top. The pages scroll inside the layout's ScrollArea, not the
 * window, so a route change would otherwise keep the previous page's offset.
 */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    // The page's own viewport only: the chat panel keeps its place across navigation.
    for (const el of document.querySelectorAll<HTMLElement>('[data-page-scroll] > [data-slot="scroll-area-viewport"]')) el.scrollTop = 0;
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppContent() {
  const { isOpen, defaultSize, onLayoutChange } = useAIPanel();
  const { isPreviewMode, isDemoMode, isLoading: configLoading } = useConfiguration();
  const isMobile = useMediaQuery(MOBILE_QUERY);

  // Wait for configuration to load before rendering anything
  if (configLoading) {
    return <Loader fullScreen />;
  }

  // In preview/demo mode, always use desktop layout to prevent remounting when iframe resizes
  // This prevents state loss (like debts) when the iframe container changes size
  const shouldUseMobileLayout = !isPreviewMode && !isDemoMode && isMobile;

  // Mobile: use simple div + drawer
  if (shouldUseMobileLayout) {
    return (
      <>
        <PreviewNavigationBlocker>
          <div className="min-h-dvh bg-background">
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/p/:code/:caseRef?" element={<ClickToPay />} />
              <Route path="/p" element={<ClickToPay />} />
              <Route path="/auth/callback" element={<CallbackPage />} />
              <Route path="/verification" element={<Navigate to="/dashboard" replace />} />
              <Route path="/cookie-policy" element={<CookiePolicy />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/account"
                element={
                  <ProtectedRoute>
                    <Account />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/payment/:debtId"
                element={
                  <ProtectedRoute>
                    <KeyedByDebt><Payment /></KeyedByDebt>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/payment-plan/:debtId"
                element={
                  <ProtectedRoute>
                    <KeyedByDebt><PaymentPlan /></KeyedByDebt>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/payment-history"
                element={
                  <ProtectedRoute>
                    <PaymentHistory />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/account-payment"
                element={
                  <ProtectedRoute>
                    <AccountPayment />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/debt/:debtId/cleared"
                element={
                  <ProtectedRoute>
                    <KeyedByDebt><DebtCleared /></KeyedByDebt>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/budget-assessment"
                element={
                  <ProtectedRoute>
                    <BudgetAssessment />
                  </ProtectedRoute>
                }
              />
              <Route path="/" element={<Navigate to="/login" replace />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </div>
          <AIMobileFullScreen context="dashboard" />
        </PreviewNavigationBlocker>
      </>
    );
  }

  // Desktop: use ResizablePanelGroup
  return (
    <>
      <PreviewNavigationBlocker>
        <ResizablePanelGroup orientation="horizontal" className="h-screen! overflow-hidden" onLayoutChange={onLayoutChange}>
          {/* Main content panel - scrolls independently */}
          <ResizablePanel id="main-content" minSize="40%" className="h-screen bg-background">
            <ScrollArea className="h-full min-h-screen" data-page-scroll>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/p/:code/:caseRef?" element={<ClickToPay />} />
                <Route path="/p" element={<ClickToPay />} />
                <Route path="/auth/callback" element={<CallbackPage />} />
                <Route path="/verification" element={<Navigate to="/dashboard" replace />} />
                <Route path="/cookie-policy" element={<CookiePolicy />} />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/account"
                  element={
                    <ProtectedRoute>
                      <Account />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/payment/:debtId"
                  element={
                    <ProtectedRoute>
                      <KeyedByDebt><Payment /></KeyedByDebt>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/payment-plan/:debtId"
                  element={
                    <ProtectedRoute>
                      <KeyedByDebt><PaymentPlan /></KeyedByDebt>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/payment-history"
                  element={
                    <ProtectedRoute>
                      <PaymentHistory />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/account-payment"
                  element={
                    <ProtectedRoute>
                      <AccountPayment />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/debt/:debtId/cleared"
                  element={
                    <ProtectedRoute>
                      <KeyedByDebt><DebtCleared /></KeyedByDebt>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/budget-assessment"
                  element={
                    <ProtectedRoute>
                      <BudgetAssessment />
                    </ProtectedRoute>
                  }
                />
                <Route path="/" element={<Navigate to="/login" replace />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </ScrollArea>
          </ResizablePanel>

          {/* AI Panel - conditionally rendered, max-height is screen size */}
          {isOpen && (
            <>
              <ResizableHandle withHandle className="z-10 bg-transparent -mr-px my-6" />
              <ResizablePanel id="ai-panel" minSize="15%" maxSize="50%" defaultSize={`${defaultSize}%`} className="h-screen z-2">
                <AIPanelContent context="dashboard" />
              </ResizablePanel>
            </>
          )}
        </ResizablePanelGroup>

        {/* Global AI Trigger Button - shows when panel is closed */}
        <AITriggerButton />
      </PreviewNavigationBlocker>
    </>
  );
}

function App() {
  return (
    <ConfigurationProvider>
      <DebtDataProvider>
        <ThemeProvider defaultTheme="light" storageKey="customer-portal-theme">
          <LanguageProvider>
            <AIContextProvider>
              <CookieConsentProvider>
                <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                  <ScrollToTop />
                  <AppContent />
                </BrowserRouter>
              </CookieConsentProvider>
            </AIContextProvider>
          </LanguageProvider>
        </ThemeProvider>
      </DebtDataProvider>
    </ConfigurationProvider>
  );
}

export default App;
